import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { apiClient, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = ["mirrors", "floorboards", "apparel"];
const FULFILLMENT = ["pending", "processing", "shipped", "delivered", "cancelled"];

const EMPTY_PRODUCT = {
  name: "",
  slug: "",
  category: "mirrors",
  price: 0,
  description: "",
  images: [""],
  variants: [],
  in_stock: true,
  featured: false,
  tags: [],
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [editor, setEditor] = useState({ open: false, mode: "create", data: EMPTY_PRODUCT });

  useEffect(() => { refresh(); }, []);

  async function refresh() {
    try {
      const [p, o, i, q] = await Promise.all([
        apiClient.get("/products"),
        apiClient.get("/admin/orders"),
        apiClient.get("/admin/inquiries"),
        apiClient.get("/admin/quote-requests"),
      ]);
      setProducts(p.data);
      setOrders(o.data);
      setInquiries(i.data);
      setQuoteRequests(q.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function updateQuoteRequest(id, patch) {
    try {
      await apiClient.put(`/admin/quote-requests/${id}`, patch);
      toast.success("Updated");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  function openCreate() {
    setEditor({ open: true, mode: "create", data: { ...EMPTY_PRODUCT, images: [""] } });
  }

  function openEdit(p) {
    setEditor({
      open: true,
      mode: "edit",
      data: { ...p, images: p.images?.length ? p.images : [""] },
    });
  }

  async function saveProduct(payload) {
    try {
      const body = {
        ...payload,
        price: Number(payload.price),
        images: (payload.images || []).filter((s) => s && s.trim().length > 0),
        tags: typeof payload.tags === "string"
          ? payload.tags.split(",").map((t) => t.trim()).filter(Boolean)
          : payload.tags,
      };
      if (!body.slug) body.slug = body.name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
      if (editor.mode === "create") {
        await apiClient.post("/admin/products", body);
        toast.success("Product created");
      } else {
        await apiClient.put(`/admin/products/${payload.id}`, body);
        toast.success("Product updated");
      }
      setEditor((s) => ({ ...s, open: false }));
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function deleteProduct(id) {
    if (!window.confirm("Delete this product?")) return;
    try {
      await apiClient.delete(`/admin/products/${id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function updateFulfillment(id, status) {
    try {
      await apiClient.put(`/admin/orders/${id}/fulfillment`, { status });
      toast.success("Order updated");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-[#0a0a0a]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-xl tracking-wider">
            BGC <span className="text-[#c8102e]">ADMIN</span>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-[#9ca3af]">{user?.email}</span>
            <button onClick={logout} className="btn-ghost py-2 px-4 text-xs" data-testid="admin-logout">
              <LogOut className="w-3.5 h-3.5" /> LOGOUT
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <h1 className="font-display text-5xl tracking-tight uppercase">Dashboard</h1>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-8">
          <Stat label="Products" value={products.length} />
          <Stat label="Orders" value={orders.length} />
          <Stat label="Paid" value={orders.filter((o) => o.payment_status === "paid").length} />
          <Stat label="Inquiries" value={inquiries.length} />
          <Stat label="Quotes" value={quoteRequests.length} />
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-10">
          <TabsList className="bg-[#0a0a0a] border border-white/10">
            <TabsTrigger value="products" data-testid="tab-products" className="font-display tracking-wider uppercase data-[state=active]:bg-[#c8102e] data-[state=active]:text-white">Products</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders" className="font-display tracking-wider uppercase data-[state=active]:bg-[#c8102e] data-[state=active]:text-white">Orders</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-inquiries" className="font-display tracking-wider uppercase data-[state=active]:bg-[#c8102e] data-[state=active]:text-white">Inquiries</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="tab-quotes" className="font-display tracking-wider uppercase data-[state=active]:bg-[#c8102e] data-[state=active]:text-white">Quote Requests</TabsTrigger>
          </TabsList>

          {/* PRODUCTS TAB */}
          <TabsContent value="products" className="mt-6">
            <div className="flex justify-between mb-4">
              <p className="text-[#9ca3af] text-sm">{products.length} total</p>
              <button onClick={openCreate} className="btn-primary" data-testid="new-product-button">
                <Plus className="w-4 h-4" /> NEW PRODUCT
              </button>
            </div>
            <div className="border border-white/10 bg-[#0a0a0a]">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-[#9ca3af] uppercase tracking-widest text-[10px]">Name</TableHead>
                    <TableHead className="text-[#9ca3af] uppercase tracking-widest text-[10px]">Category</TableHead>
                    <TableHead className="text-[#9ca3af] uppercase tracking-widest text-[10px]">Price</TableHead>
                    <TableHead className="text-[#9ca3af] uppercase tracking-widest text-[10px]">Stock</TableHead>
                    <TableHead className="text-[#9ca3af] uppercase tracking-widest text-[10px]">Featured</TableHead>
                    <TableHead className="text-right text-[#9ca3af] uppercase tracking-widest text-[10px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((p) => (
                    <TableRow key={p.id} className="border-white/5 hover:bg-white/5" data-testid={`product-row-${p.id}`}>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell className="text-[#9ca3af] capitalize">{p.category}</TableCell>
                      <TableCell>${p.price.toFixed(2)}</TableCell>
                      <TableCell>{p.in_stock ? "Yes" : "No"}</TableCell>
                      <TableCell>{p.featured ? "★" : "—"}</TableCell>
                      <TableCell className="text-right space-x-2">
                        <button onClick={() => openEdit(p)} className="text-[#9ca3af] hover:text-white p-2" data-testid={`edit-product-${p.id}`}>
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => deleteProduct(p.id)} className="text-[#9ca3af] hover:text-[#c8102e] p-2" data-testid={`delete-product-${p.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* ORDERS TAB */}
          <TabsContent value="orders" className="mt-6">
            <div className="border border-white/10 bg-[#0a0a0a]">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-[#9ca3af] uppercase tracking-widest text-[10px]">Order</TableHead>
                    <TableHead className="text-[#9ca3af] uppercase tracking-widest text-[10px]">Customer</TableHead>
                    <TableHead className="text-[#9ca3af] uppercase tracking-widest text-[10px]">Total</TableHead>
                    <TableHead className="text-[#9ca3af] uppercase tracking-widest text-[10px]">Payment</TableHead>
                    <TableHead className="text-[#9ca3af] uppercase tracking-widest text-[10px]">Fulfillment</TableHead>
                    <TableHead className="text-[#9ca3af] uppercase tracking-widest text-[10px]">Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-[#9ca3af] py-12">No orders yet</TableCell></TableRow>
                  )}
                  {orders.map((o) => (
                    <TableRow key={o.id} className="border-white/5 hover:bg-white/5" data-testid={`order-row-${o.id}`}>
                      <TableCell className="font-mono text-xs">{o.id.slice(0, 8)}</TableCell>
                      <TableCell>{o.customer_name}<br /><span className="text-xs text-[#9ca3af]">{o.customer_email}</span></TableCell>
                      <TableCell>${o.total.toFixed(2)}</TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-1 ${o.payment_status === "paid" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}`}>
                          {o.payment_status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Select value={o.fulfillment_status} onValueChange={(v) => updateFulfillment(o.id, v)}>
                          <SelectTrigger className="w-36 bg-[#0a0a0a] border-white/10" data-testid={`fulfillment-select-${o.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                            {FULFILLMENT.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-xs text-[#9ca3af]">{new Date(o.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* INQUIRIES TAB */}
          <TabsContent value="inquiries" className="mt-6">
            <div className="space-y-3">
              {inquiries.length === 0 && <p className="text-[#9ca3af]">No messages yet.</p>}
              {inquiries.map((i) => (
                <div key={i.id} className="border border-white/10 bg-[#0a0a0a] p-5" data-testid={`inquiry-${i.id}`}>
                  <div className="flex justify-between gap-4">
                    <div>
                      <p className="font-display text-lg uppercase tracking-wider">{i.subject}</p>
                      <p className="text-xs text-[#9ca3af] mt-1">{i.name} · {i.email}</p>
                    </div>
                    <p className="text-xs text-[#6b7280]">{new Date(i.created_at).toLocaleString()}</p>
                  </div>
                  <p className="text-[#d1d5db] mt-3 whitespace-pre-line">{i.message}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* QUOTE REQUESTS TAB */}
          <TabsContent value="quotes" className="mt-6">
            <div className="space-y-3">
              {quoteRequests.length === 0 && <p className="text-[#9ca3af]">No quote requests yet.</p>}
              {quoteRequests.map((q) => (
                <QuoteCard key={q.id} q={q} onUpdate={(patch) => updateQuoteRequest(q.id, patch)} />
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <ProductEditor
        editor={editor}
        onClose={() => setEditor((s) => ({ ...s, open: false }))}
        onSave={saveProduct}
      />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-white/10 p-5 bg-[#0a0a0a]">
      <p className="bgc-eyebrow">{label}</p>
      <p className="font-display text-4xl mt-2">{value}</p>
    </div>
  );
}

function ProductEditor({ editor, onClose, onSave }) {
  const [data, setData] = useState(EMPTY_PRODUCT);

  useEffect(() => {
    if (editor.open) {
      setData({
        ...editor.data,
        tags: Array.isArray(editor.data.tags) ? editor.data.tags.join(", ") : (editor.data.tags || ""),
      });
    }
  }, [editor.open, editor.data]);

  function set(k, v) { setData((d) => ({ ...d, [k]: v })); }
  function setImage(i, v) {
    setData((d) => {
      const imgs = [...d.images];
      imgs[i] = v;
      return { ...d, images: imgs };
    });
  }

  return (
    <Dialog open={editor.open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl tracking-wider uppercase">
            {editor.mode === "create" ? "New Product" : "Edit Product"}
          </DialogTitle>
          <DialogDescription className="text-[#9ca3af]">
            Fill in the details below and click save to publish your changes.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <Lbl label="Name">
            <input data-testid="editor-name" className="input-dark w-full" value={data.name || ""} onChange={(e) => set("name", e.target.value)} />
          </Lbl>
          <Lbl label="Slug (optional)">
            <input data-testid="editor-slug" className="input-dark w-full" value={data.slug || ""} onChange={(e) => set("slug", e.target.value)} />
          </Lbl>
          <Lbl label="Category">
            <select data-testid="editor-category" className="input-dark w-full" value={data.category} onChange={(e) => set("category", e.target.value)}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Lbl>
          <Lbl label="Price (USD)">
            <input data-testid="editor-price" type="number" step="0.01" className="input-dark w-full" value={data.price} onChange={(e) => set("price", e.target.value)} />
          </Lbl>
          <Lbl label="Description" className="md:col-span-2">
            <textarea data-testid="editor-description" rows={4} className="input-dark w-full resize-none" value={data.description || ""} onChange={(e) => set("description", e.target.value)} />
          </Lbl>
          <Lbl label="Image URLs" className="md:col-span-2">
            {(data.images || [""]).map((img, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input data-testid={`editor-image-${i}`} className="input-dark flex-1" placeholder="https://…" value={img} onChange={(e) => setImage(i, e.target.value)} />
                {i === (data.images?.length || 1) - 1 && (
                  <button type="button" onClick={() => set("images", [...(data.images || []), ""])} className="btn-ghost text-xs px-3" data-testid="editor-add-image">+ ADD</button>
                )}
              </div>
            ))}
          </Lbl>
          <Lbl label="Tags (comma-separated)" className="md:col-span-2">
            <input data-testid="editor-tags" className="input-dark w-full" value={data.tags || ""} onChange={(e) => set("tags", e.target.value)} />
          </Lbl>
          <label className="flex items-center gap-3 text-sm">
            <input data-testid="editor-instock" type="checkbox" checked={!!data.in_stock} onChange={(e) => set("in_stock", e.target.checked)} /> In stock
          </label>
          <label className="flex items-center gap-3 text-sm">
            <input data-testid="editor-featured" type="checkbox" checked={!!data.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured
          </label>
        </div>

        <DialogFooter className="mt-6">
          <button onClick={onClose} className="btn-ghost" data-testid="editor-cancel">CANCEL</button>
          <button onClick={() => onSave({ ...data, price: Number(data.price) })} className="btn-primary" data-testid="editor-save">SAVE</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Lbl({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="bgc-eyebrow block mb-2">{label}</span>
      {children}
    </label>
  );
}


const QUOTE_STATUSES = ["new", "reviewing", "quoted", "accepted", "declined"];

function QuoteCard({ q, onUpdate }) {
  const [draftAmount, setDraftAmount] = useState(q.quote_amount ?? "");
  const [draftNotes, setDraftNotes] = useState(q.admin_notes ?? "");

  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-5" data-testid={`quote-${q.id}`}>
      <div className="flex justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <p className="font-display text-lg uppercase tracking-wider">{q.title}</p>
            <span className="text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 bg-[#c8102e]/15 text-[#c8102e] border border-[#c8102e]/40">{q.product_type}</span>
          </div>
          <p className="text-xs text-[#9ca3af] mt-1">{q.name} · {q.email}{q.phone ? ` · ${q.phone}` : ""}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={q.status} onValueChange={(v) => onUpdate({ status: v })}>
            <SelectTrigger className="w-32 bg-[#0a0a0a] border-white/10" data-testid={`quote-status-${q.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
              {QUOTE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-[#6b7280] whitespace-nowrap">{new Date(q.created_at).toLocaleString()}</p>
        </div>
      </div>

      <p className="text-[#d1d5db] mt-3 whitespace-pre-line">{q.description}</p>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4 text-xs text-[#9ca3af]">
        {q.finish_color && <Meta label="Finish" value={q.finish_color} />}
        {q.motorcycle && <Meta label="Motorcycle" value={q.motorcycle} />}
        {q.budget && <Meta label="Budget" value={q.budget} />}
        {q.timeline && <Meta label="Timeline" value={q.timeline} />}
      </div>

      {q.reference_images?.length > 0 && (
        <div className="flex gap-2 mt-4">
          {q.reference_images.map((src, i) => (
            <a key={i} href={src} target="_blank" rel="noreferrer" className="block w-24 h-24 border border-white/10" data-testid={`quote-${q.id}-ref-${i}`}>
              <img src={src} alt={`ref-${i}`} className="w-full h-full object-cover" />
            </a>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5 pt-4 border-t border-white/5">
        <label className="block md:col-span-1">
          <span className="bgc-eyebrow block mb-2">Quote ($)</span>
          <input
            type="number" step="0.01" className="input-dark w-full"
            value={draftAmount} onChange={(e) => setDraftAmount(e.target.value)}
            data-testid={`quote-${q.id}-amount`}
          />
        </label>
        <label className="block md:col-span-2">
          <span className="bgc-eyebrow block mb-2">Admin notes</span>
          <textarea
            rows={2} className="input-dark w-full resize-none"
            value={draftNotes} onChange={(e) => setDraftNotes(e.target.value)}
            data-testid={`quote-${q.id}-notes`}
          />
        </label>
      </div>
      <div className="mt-3 text-right">
        <button
          onClick={() => onUpdate({ quote_amount: draftAmount ? Number(draftAmount) : null, admin_notes: draftNotes || null })}
          className="btn-primary text-xs"
          data-testid={`quote-${q.id}-save`}
        >
          SAVE QUOTE
        </button>
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <p className="bgc-eyebrow text-[10px]">{label}</p>
      <p className="text-white mt-1 text-sm">{value}</p>
    </div>
  );
}
